import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { DASHBOARD } from '@/constants/testIds';
import SEO from '@/components/dubinko/SEO';
import {
  Calendar, MapPin, Plus, Sparkles, ArrowRight, X, Edit3,
  Star, MessageSquareText, History as HistoryIcon, Send, CheckCircle2
} from 'lucide-react';

const STATUS_LABEL = {
  pending: { t: 'Na čekanju', c: 'bg-amber-100 text-amber-800' },
  confirmed: { t: 'Potvrđeno', c: 'bg-blue-100 text-blue-800' },
  completed: { t: 'Završeno', c: 'bg-emerald-100 text-emerald-800' },
  cancelled: { t: 'Otkazano', c: 'bg-slate-200 text-slate-700' },
};

const SERVICE_LABEL = {
  residential: 'Stan / kuća',
  commercial: 'Poslovni prostor',
  carpet: 'Tepisi & tapacirung',
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [b, r, m] = await Promise.all([
        api.get('/bookings/my'),
        api.get('/reviews/mine'),
        api.get('/messages/mine'),
      ]);
      setBookings(b.data); setReviews(r.data); setMessages(m.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const upcoming = bookings.filter((b) => ['pending', 'confirmed'].includes(b.status));
  const past = bookings.filter((b) => ['completed', 'cancelled'].includes(b.status));

  const TABS = [
    { id: 'upcoming', label: 'Predstojeće', icon: Calendar, tid: DASHBOARD.tabUpcoming, count: upcoming.length },
    { id: 'history', label: 'Istorija', icon: HistoryIcon, tid: DASHBOARD.tabHistory, count: past.length },
    { id: 'reviews', label: 'Recenzije', icon: Star, tid: DASHBOARD.tabReviews, count: reviews.length },
    { id: 'questions', label: 'Pitanja', icon: MessageSquareText, tid: DASHBOARD.tabQuestions, count: messages.length },
  ];

  return (
    <main data-testid={DASHBOARD.root}>
      <SEO title="Moj nalog" description="Pregled vaših rezervacija, recenzija i pitanja u Dubinko." path="/nalog" />

      <section className="section-pad aurora-bg grain">
        <div className="container-x">
          <span className="tag-pill">Moj nalog</span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold mt-4">Zdravo, {user?.name}</h1>
          <p className="text-slate-600 mt-3 text-lg">Sve o vašim rezervacijama, na jednom mestu.</p>
          <Link to="/rezervacija" className="btn-primary mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold">
            <Plus className="w-4 h-4" /> Nova rezervacija
          </Link>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-6">
            {TABS.map((t) => {
              const I = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} data-testid={t.tid} onClick={() => setTab(t.id)}
                  className={`px-4 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 transition ${
                    active ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-400'
                  }`}>
                  <I className="w-4 h-4" /> {t.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-slate-100'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {loading ? <div className="text-slate-500">Učitavanje…</div> : (
            <>
              {tab === 'upcoming' && (
                <BookingList items={upcoming} emptyText="Nema predstojećih rezervacija." onChange={refresh} editable />
              )}
              {tab === 'history' && (
                <BookingList items={past} emptyText="Još uvek nemate istorije čišćenja." onChange={refresh} canReview />
              )}
              {tab === 'reviews' && <MyReviews reviews={reviews} />}
              {tab === 'questions' && <MyQuestions messages={messages} bookings={bookings} onChange={refresh} />}
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function BookingList({ items, emptyText, onChange, editable, canReview }) {
  if (items.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
        <Sparkles className="w-10 h-10 mx-auto text-purple-600" />
        <h3 className="font-display text-xl font-bold mt-3">{emptyText}</h3>
        <Link to="/rezervacija" className="btn-primary mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold">
          Zakaži <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={DASHBOARD.bookingsList}>
      {items.map((b) => (
        <BookingCard key={b.id} b={b} onChange={onChange} editable={editable} canReview={canReview} />
      ))}
    </div>
  );
}

function BookingCard({ b, onChange, editable, canReview }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showResched, setShowResched] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [date, setDate] = useState(b.preferred_date);
  const [time, setTime] = useState(b.preferred_time);

  const st = STATUS_LABEL[b.status] || STATUS_LABEL.pending;
  const minDate = new Date().toISOString().slice(0, 10);

  const cancel = async () => {
    if (!window.confirm('Otkazati rezervaciju?')) return;
    setBusy(true); setError('');
    try { await api.post(`/bookings/${b.id}/cancel`); onChange(); }
    catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reschedule = async () => {
    setBusy(true); setError('');
    try { await api.post(`/bookings/${b.id}/reschedule`, { preferred_date: date, preferred_time: time }); setShowResched(false); onChange(); }
    catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={DASHBOARD.bookingItem}
      className="bg-white border border-slate-200 rounded-3xl p-6 hover:shadow-md transition">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{SERVICE_LABEL[b.service_type] || b.service_type}</div>
          <h3 className="font-display text-lg font-bold mt-1">{b.property_size}</h3>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.c}`}>{st.t}</span>
      </div>
      <div className="mt-4 space-y-2 text-sm text-slate-600">
        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-700" /> {b.preferred_date} · {b.preferred_time}</div>
        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-600" /> {b.address}, {b.city}</div>
        {b.estimated_price && (
          <div className="text-slate-500">Procena: <strong className="text-slate-900">{Number(b.estimated_price).toFixed(0)} €</strong>
            {b.payment_status === 'paid' && <span className="ml-2 inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Plaćeno</span>}
          </div>
        )}
      </div>

      {error && <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{error}</div>}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link to={`/rezervacija?service=${b.service_type}`} data-testid={DASHBOARD.rebookBtn}
              className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
          Zakaži ponovo <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        {editable && (
          <>
            <button data-testid={DASHBOARD.rescheduleBtn} onClick={() => setShowResched((v) => !v)}
              className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
              <Edit3 className="w-3.5 h-3.5" /> Pomeri termin
            </button>
            <button data-testid={DASHBOARD.cancelBtn} disabled={busy} onClick={cancel}
              className="px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60">
              <X className="w-3.5 h-3.5" /> Otkaži
            </button>
          </>
        )}
        {canReview && b.status === 'completed' && !b.has_review && (
          <button data-testid={DASHBOARD.reviewOpenBtn} onClick={() => setShowReviewForm((v) => !v)}
            className="btn-green px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Star className="w-3.5 h-3.5" /> Ostavi recenziju
          </button>
        )}
        {b.has_review && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Hvala na recenziji</span>
        )}
      </div>

      {showResched && (
        <div className="mt-4 p-4 bg-slate-50 rounded-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Datum</label>
              <input data-testid={DASHBOARD.rescheduleDate} type="date" min={minDate} value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Vreme</label>
              <select data-testid={DASHBOARD.rescheduleTime} value={time} onChange={(e) => setTime(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white">
                {['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button data-testid={DASHBOARD.rescheduleConfirm} onClick={reschedule} disabled={busy}
              className="btn-primary px-4 py-2 rounded-full text-sm font-semibold">Potvrdi</button>
            <button onClick={() => setShowResched(false)} className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold">Otkaži</button>
          </div>
        </div>
      )}

      {showReviewForm && <InlineReviewForm bookingId={b.id} onDone={() => { setShowReviewForm(false); onChange(); }} />}
    </div>
  );
}

function InlineReviewForm({ bookingId, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await api.post('/reviews', { booking_id: bookingId, rating, text });
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-2xl">
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map((n) => (
          <button key={n} data-testid={`${DASHBOARD.reviewRating}-${n}`}
            onClick={() => setRating(n)} type="button">
            <Star className={`w-7 h-7 ${n <= rating ? 'fill-amber-500 text-amber-500' : 'text-slate-300'}`} />
          </button>
        ))}
      </div>
      <textarea data-testid={DASHBOARD.reviewText} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Podelite iskustvo…" rows={3}
        className="mt-3 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-blue-700 resize-none" />
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
      <div className="mt-3 flex gap-2">
        <button data-testid={DASHBOARD.reviewSubmit} onClick={submit} disabled={busy || text.length < 5}
          className="btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-50">
          Pošalji recenziju
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-2">Recenziju će pregledati administrator pre objave.</p>
    </div>
  );
}

function MyReviews({ reviews }) {
  if (reviews.length === 0) return (
    <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
      <Star className="w-10 h-10 mx-auto text-amber-500" />
      <h3 className="font-display text-xl font-bold mt-3">Još uvek nemate recenzija</h3>
      <p className="text-slate-600 mt-2">Nakon završenog čišćenja moći ćete da ostavite utisak.</p>
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {reviews.map((r) => (
        <div key={r.id} className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="flex gap-0.5 text-amber-500">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'fill-amber-500' : ''}`} />)}
          </div>
          <p className="text-slate-700 mt-3">&ldquo;{r.text}&rdquo;</p>
          <div className="text-xs text-slate-500 mt-3 flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'hidden' ? 'bg-slate-200' : 'bg-amber-100 text-amber-800'}`}>
              {r.status === 'approved' ? 'Objavljeno' : r.status === 'hidden' ? 'Sakriveno' : 'Čeka odobrenje'}
            </span>
            <span>{new Date(r.created_at).toLocaleDateString('sr-RS')}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyQuestions({ messages, bookings, onChange }) {
  const [subject, setSubject] = useState('Pitanje');
  const [bookingId, setBookingId] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/messages', { subject, booking_id: bookingId || null, message: text });
      setText(''); onChange();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-bold">Postavi pitanje</h3>
        <p className="text-sm text-slate-600 mt-1">Odgovaramo u roku od nekoliko sati.</p>
        <div className="mt-4 space-y-3">
          <input data-testid={DASHBOARD.questionSubject} value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Tema" className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
          {bookings.length > 0 && (
            <select value={bookingId} onChange={(e) => setBookingId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 bg-white">
              <option value="">Bez veze sa rezervacijom</option>
              {bookings.map((b) => (
                <option key={b.id} value={b.id}>{SERVICE_LABEL[b.service_type]} · {b.preferred_date}</option>
              ))}
            </select>
          )}
          <textarea data-testid={DASHBOARD.questionMessage} required minLength={2} rows={4}
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Vaše pitanje…"
            className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
          {error && <div className="text-xs text-red-700">{error}</div>}
          <button data-testid={DASHBOARD.questionSubmit} type="submit" disabled={busy}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold disabled:opacity-60">
            <Send className="w-4 h-4" /> Pošalji
          </button>
        </div>
      </form>

      <div className="space-y-3">
        {messages.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-6 text-slate-500 text-sm">
            Nema poslatih pitanja.
          </div>
        ) : messages.map((m) => (
          <div key={m.id} className="bg-white border border-slate-200 rounded-3xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="font-display font-bold">{m.subject}</div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.status === 'answered' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {m.status === 'answered' ? 'Odgovoreno' : 'Otvoreno'}
              </span>
            </div>
            <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{m.message}</p>
            {m.admin_reply && (
              <div className="mt-3 p-3 rounded-xl bg-slate-50 border-l-4 border-emerald-500">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Odgovor administracije</div>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.admin_reply}</p>
              </div>
            )}
            <div className="text-xs text-slate-500 mt-3">{new Date(m.created_at).toLocaleString('sr-RS')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
